import type { GoalRecord } from '~/models/goal'
import type { PhaseRecord } from '~/models/phase'
import type { WeeklyReviewRecord } from '~/models/weeklyReview'
import { planProjectBackfill } from '~/storage/migrations/backfillProject'
import { STORE } from '~/storage/schema'
import type { BackupPayload } from '../format'
import type { PayloadMigration } from './index'

/**
 * Version 2 — the paired counterpart of the database migration (D-031).
 *
 * A version-1 backup restored into a version-2 database would otherwise arrive
 * with no `projects` store and no `projectId` anywhere, and no upgrade would
 * ever fire to fix it: the live database is already at 2. The records would sit
 * there orphaned, invisible to every project query, and nothing would report a
 * failure.
 *
 * The decision — one project, left active, named from the target date — is
 * `planProjectBackfill`, shared verbatim with `storage/migrations/v2.ts`. All
 * that differs here is the plumbing: arrays in memory instead of cursors in a
 * `versionchange` transaction. A merge-blocking test asserts the two registries
 * stay in step, and a paired-equivalence test asserts the two paths produce the
 * same records from the same input.
 */
export const payloadMigrationV2: PayloadMigration = {
  to: 2,
  describe: 'Add the projects store and carry existing phases, goals and reviews into one project',
  migrate(payload: BackupPayload): BackupPayload {
    const plan = planProjectBackfill({
      phases: (payload.stores[STORE.phases] ?? []) as PhaseRecord[],
      goals: (payload.stores[STORE.goals] ?? []) as GoalRecord[],
      weeklyReviews: (payload.stores[STORE.weeklyReviews] ?? []) as WeeklyReviewRecord[],
    })

    /* A backup of a fresh install carries nothing to place in a project. The
       `projects` store still has to appear, because validation checks the
       payload against the current schema and a missing store is a gap rather
       than an empty one. */
    if (plan === undefined) {
      return { ...payload, stores: { ...payload.stores, [STORE.projects]: [] } }
    }

    return {
      ...payload,
      stores: {
        ...payload.stores,
        [STORE.projects]: [plan.project],
        [STORE.phases]: plan.phases,
        [STORE.goals]: plan.goals,
        [STORE.weeklyReviews]: plan.weeklyReviews,
      },
    }
  },
}
